'use client';

import { useCallback, useEffect, useMemo, useReducer } from 'react';
import type { CartItem, CartSubselection } from '@/features/totems/domain/model/cart-item';
import { calculateCartTotals } from '@/features/totems/domain/service/totem-price';

export type { CartItem, CartSubselection };

type AddItemPayload = Omit<CartItem, 'id'>;

type CartState = {
  items: CartItem[];
  discount: {
    id: string;
    code: string;
    amount: number; // percentage (e.g. 10 for 10%) or fixed amount depending on implementation
    type: 'percentage' | 'fixed'; // assuming these types, we'll adapt later if needed
    method: 'discount' | 'promo';
    minPurchase: number | null;
  } | null;
};

type CartAction =
  | { type: 'ADD_ITEM'; payload: AddItemPayload }
  | { type: 'UPDATE_ITEM'; payload: { id: string; item: AddItemPayload } }
  | { type: 'REMOVE_ITEM'; payload: { id: string } }
  | { type: 'UPDATE_QUANTITY'; payload: { id: string; quantity: number } }
  | { type: 'SET_DISCOUNT'; payload: CartState['discount'] }
  | { type: 'CLEAR' };

function areSubselectionsEqual(a: CartSubselection[], b: CartSubselection[]): boolean {
  if (a.length !== b.length) return false;

  const aSorted = [...a].sort((x, y) => x.subselectionItemId.localeCompare(y.subselectionItemId));
  const bSorted = [...b].sort((x, y) => x.subselectionItemId.localeCompare(y.subselectionItemId));

  for (let i = 0; i < aSorted.length; i++) {
    if (aSorted[i].subselectionItemId !== bSorted[i].subselectionItemId) return false;
    if (aSorted[i].quantity !== bSorted[i].quantity) return false;
  }
  return true;
}

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existingItemIndex = state.items.findIndex(
        item =>
          item.productId === action.payload.productId &&
          areSubselectionsEqual(item.subselections, action.payload.subselections)
      );

      if (existingItemIndex !== -1) {
        const newItems = [...state.items];
        newItems[existingItemIndex] = {
          ...newItems[existingItemIndex],
          quantity: newItems[existingItemIndex].quantity + action.payload.quantity,
        };
        return {
          ...state,
          items: newItems,
        };
      }

      return {
        ...state,
        items: [
          ...state.items,
          {
            ...action.payload,
            id: crypto.randomUUID(),
          },
        ],
      };
    }
    case 'UPDATE_ITEM':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id ? { ...action.payload.item, id: action.payload.id } : item
        ),
      };
    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload.id),
      };
    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id ? { ...item, quantity: Math.max(1, action.payload.quantity) } : item
        ),
      };
    case 'SET_DISCOUNT':
      return {
        ...state,
        discount: action.payload,
      };
    case 'CLEAR':
      return {
        items: [],
        discount: null,
      };
    default:
      return state;
  }
}

export function useTotemCart() {
  const [state, dispatch] = useReducer(cartReducer, { items: [], discount: null });

  const addItem = useCallback((payload: AddItemPayload) => {
    dispatch({ type: 'ADD_ITEM', payload });
  }, []);

  const updateItem = useCallback((id: string, item: AddItemPayload) => {
    dispatch({ type: 'UPDATE_ITEM', payload: { id, item } });
  }, []);

  const removeItem = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: { id } });
  }, []);

  const updateQuantity = useCallback((id: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity } });
  }, []);

  const applyDiscount = useCallback((discount: CartState['discount']) => {
    dispatch({ type: 'SET_DISCOUNT', payload: discount });
  }, []);

  const clearDiscount = useCallback(() => {
    dispatch({ type: 'SET_DISCOUNT', payload: null });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR' });
  }, []);

  // Totales con la regla canónica (promos ya vienen en `unitPrice`; cupón truncado antes de restar).
  const totals = useMemo(() => calculateCartTotals(state.items, state.discount), [state.items, state.discount]);
  const subtotal = totals.subtotal;
  const total = totals.total;
  const subtotalBruto = totals.subtotalBruto;

  // Auto-remove discount if subtotal drops below minPurchase
  useEffect(() => {
    if (state.discount?.minPurchase != null && subtotal < state.discount.minPurchase) {
      clearDiscount();
      // We could add a toast here, but use-totem-cart doesn't currently import toast.
      // The order panel will reflect the change as discount will become null.
    }
  }, [subtotal, state.discount?.minPurchase, clearDiscount]);

  const itemCount = useMemo(() => state.items.reduce((sum, item) => sum + item.quantity, 0), [state.items]);

  const toOrderPayload = useCallback(() => {
    return state.items.map(item => ({
      product_id: item.productId,
      quantity: item.quantity,
      subselections: item.subselections.map((sub: CartSubselection) => ({
        subselectionItemId: sub.subselectionItemId,
        quantity: sub.quantity,
      })),
    }));
  }, [state.items]);

  return useMemo(
    () => ({
      items: state.items,
      discount: state.discount,
      subtotal,
      subtotalBruto,
      total,
      itemCount,
      addItem,
      updateItem,
      removeItem,
      updateQuantity,
      applyDiscount,
      clearDiscount,
      clearCart,
      toOrderPayload,
    }),
    [
      state.items,
      state.discount,
      subtotal,
      subtotalBruto,
      total,
      itemCount,
      addItem,
      updateItem,
      removeItem,
      updateQuantity,
      applyDiscount,
      clearDiscount,
      clearCart,
      toOrderPayload,
    ]
  );
}
